/**
 * HouseLinkPrompt — PROPERTY-LINK-01 (the approved mock's yellow strip), redrawn as a
 * COMPARE (HOUSE-COMPARE-01, Seth 2026-10-22: "one is Way and one is Drive … I hate that
 * I have to go and find and look up both accounts manually").
 *
 * "New account at a known address — 300 Lake Street W was The Landschute
 * Group's job site (builder). We were here Nov 5, 2024. Same house changing
 * hands?"  Three answers, one tap each:
 *   · Yes — they took over   → records the handoff (property + both tenures)
 *   · Not the same house     → unlinks the pair (a duplex, an ADU, two streets that share a name)
 *   · Same person twice      → unlinks the pair and points at the merge tool
 * The full card shows BOTH accounts side by side — name · # · address · when we
 * were there · phone · email · equipment — each with Open (back-link) and ⧉ (new
 * tab), and says it plainly when the two addresses are not the same street.
 * Shown on the 360 header the morning the account appears (compact), on Today's
 * Service queue, and in Settings → Addresses as the review list. Nothing moves and
 * nothing in ESC changes — only a Pegasus fact gets written.
 */
import { useState } from "react";
import { Home, Check, Loader2, ExternalLink, Phone, Mail, Wrench, ArrowLeftRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { CustomerLink } from "@/components/links";
import { useHouseAnswer, surnameKey, sameStreet, streetTypeOf, useHousePairFacts, type HouseAnswer, type HouseReviewRow } from "./houseHistory";

const dLong = (iso: string | null | undefined) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
const prettyPhone = (s: string) => { const d = s.replace(/\D/g, ""); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s; };

/** "Kelley & Laurie Gale" → "the Gales" is a guess we won't make; use the name as given. */
const shortName = (name: string) => (name.length > 28 ? `${name.slice(0, 27).trimEnd()}…` : name);

export function HouseLinkPrompt({ row, compact = false, onAnswered }: {
  row: HouseReviewRow;
  /** The 360 header's one-liner (no address repeat, tighter paddings). */
  compact?: boolean;
  onAnswered?: (answer: HouseAnswer) => void;
}) {
  const answer = useHouseAnswer();
  const [done, setDone] = useState<HouseAnswer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const who = row.other_is_company ? `${row.other_name}'s job site (builder)` : `${row.other_name}'s home`;
  const visits = row.other_visits === 1
    ? `We were here ${dLong(row.other_last) ?? "once"}.`
    : `We were here ${row.other_visits} times${row.other_last ? `, last ${dLong(row.other_last)}` : ""}.`;
  // Same surname on both accounts = almost always the same household entered twice.
  const sameName = !row.other_is_company && surnameKey(row.name) !== "" && surnameKey(row.name) === surnameKey(row.other_name);
  // The two addresses as ESC has them — different street types (Court vs Way) read as two streets.
  const otherAddr = row.other_address ?? null;
  const streetsDiffer = !!otherAddr && !!row.address && !sameStreet(row.address, otherAddr);
  const typeA = streetTypeOf(row.address), typeB = streetTypeOf(otherAddr);
  const differWords = streetsDiffer ? (typeA && typeB && typeA !== typeB ? `${typeA} vs ${typeB}` : "different streets") : null;
  const sentence = sameName
    ? <>Another account under the same name, <b>{row.other_name}</b>, already has history here. {visits}</>
    : row.relation === "overlap"
      ? <>Also on file under <b>{row.other_name}</b>{row.other_is_company ? " (company)" : ""}. {visits}</>
      : <><b>{otherAddr ?? row.address ?? "This address"}</b> was {who}. {visits}</>;
  const pick = (a: HouseAnswer) => {
    setErr(null);
    answer.mutate(
      { custno: row.custno, locno: row.locno, otherCustno: row.other_custno, otherLocno: row.other_locno, answer: a },
      { onSuccess: () => { setDone(a); onAnswered?.(a); }, onError: (e) => setErr(e instanceof Error ? e.message : String(e)) },
    );
  };
  const btn = "inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-bold transition-colors disabled:opacity-50";
  const primary = "bg-slate-900 text-white hover:bg-slate-700";
  const quiet = "border border-amber-300 bg-white text-slate-700 hover:bg-amber-100/60";

  if (done) {
    return (
      <div className={cn("flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800", compact ? "px-3 py-1.5 text-[12px]" : "px-4 py-2.5 text-sm")}>
        <Check className="h-4 w-4" />
        {done === "same_house"
          ? <>Linked — <b>{row.address ?? "this address"}</b> now reads as one house: {row.other_name} before, {row.name} since.</>
          : done === "different_unit"
            ? <>Noted — not the same house. These two won't be paired again.</>
            : <>Noted — same person twice. Merge them from the account's ··· menu when you're ready.</>}
      </div>
    );
  }

  const yesLabel = sameName ? "No — a new owner, same name" : row.relation === "overlap" ? "Yes — same house" : `Yes — ${shortName(row.name)} took over`;
  const notLabel = streetsDiffer ? "Not the same house" : "Different unit";
  const dupLabel = sameName ? "Yes — same person twice" : "Same person twice";
  const buttons = (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
      <button type="button" disabled={answer.isPending} onClick={() => pick("same_house")} className={cn(btn, sameName || streetsDiffer ? quiet : primary)}>
        {answer.isPending && answer.variables?.answer === "same_house" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {yesLabel}
      </button>
      <button type="button" disabled={answer.isPending} onClick={() => pick("different_unit")} className={cn(btn, streetsDiffer ? primary : quiet)}>
        {answer.isPending && answer.variables?.answer === "different_unit" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {notLabel}
      </button>
      <button type="button" disabled={answer.isPending} onClick={() => pick("duplicate")} className={cn(btn, sameName ? primary : quiet)}>
        {answer.isPending && answer.variables?.answer === "duplicate" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {dupLabel}
      </button>
    </div>
  );

  if (compact) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Home className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-[12.5px] text-amber-900" style={{ flexBasis: "24rem" }}>
            {sentence}{" "}
            {streetsDiffer
              ? <><b>Their address is {otherAddr}</b> — {differWords}. Same house?</>
              : sameName ? "Same person twice?" : row.relation === "overlap" ? "Same house?" : "Same house changing hands?"}
            {" "}<CustomerLink custno={row.other_custno} locno={row.other_locno ?? undefined} className="text-amber-900">Open {row.other_name} →</CustomerLink>
            {err && <div className="mt-1 text-[11px] font-medium text-rose-700">{err}</div>}
          </div>
          {buttons}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Home className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm text-amber-900" style={{ flexBasis: "26rem" }}>
          <span className="font-semibold">New account at a known address</span>
          <span className="text-amber-800"> — {sentence} {sameName ? "Same person twice?" : row.relation === "overlap" ? "Same house?" : "Same house changing hands?"}</span>
        </div>
        {buttons}
      </div>
      <HouseCompare row={row} streetsDiffer={streetsDiffer} differWords={differWords} />
      {err && <div className="mt-1.5 text-[11px] font-medium text-rose-700">{err}</div>}
    </div>
  );
}

/** The two accounts side by side — so the office never has to open both to see the difference. */
function HouseCompare({ row, streetsDiffer, differWords }: { row: HouseReviewRow; streetsDiffer: boolean; differWords: string | null }) {
  const { data: facts } = useHousePairFacts(row);
  const Side = ({ tag, name, custno, locno, address, city, when, side, tone }: {
    tag: string; name: string; custno: string; locno: string | null; address: string | null; city: string | null; when: string; side: "self" | "other"; tone: "new" | "known";
  }) => {
    const f = facts?.[side];
    const href = `/customers/${custno}${locno ? `?loc=${encodeURIComponent(locno)}` : ""}`;
    return (
      <div className={cn("min-w-0 rounded-lg border bg-white px-3 py-2.5", tone === "new" ? "border-amber-200" : "border-slate-200")}>
        <div className="flex items-center gap-2">
          <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.06em]", tone === "new" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600")}>{tag}</span>
          <CustomerLink custno={custno} locno={locno ?? undefined} className="truncate text-[13.5px] font-bold text-slate-900">{name}</CustomerLink>
          <span className="font-mono text-[11px] text-slate-400">#{custno}</span>
          <a href={href} target="_blank" rel="noreferrer" title="Open in a new tab" className="ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-brand-700" onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className={cn("mt-1 text-[13px] font-semibold", streetsDiffer ? "text-rose-700" : "text-slate-800")}>{address ?? "No street address"}<span className="font-normal text-slate-500">{city ? ` · ${city}` : ""}</span></div>
        <div className="mt-0.5 text-[11.5px] text-slate-500">{when}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-slate-600">
          {f?.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{prettyPhone(f.phone)}</span> : <span className="text-slate-400">no phone</span>}
          {f?.email ? <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3 text-slate-400" />{f.email}</span> : null}
          {f ? <span className="inline-flex items-center gap-1"><Wrench className="h-3 w-3 text-slate-400" />{f.equipment === 0 ? "no equipment on file" : `${f.equipment} unit${f.equipment === 1 ? "" : "s"} on file`}</span> : null}
        </div>
      </div>
    );
  };
  const selfWhen = row.self_visits > 0
    ? `${row.self_visits} visit${row.self_visits === 1 ? "" : "s"} on the new account${row.appeared ? ` · appeared ${dLong(row.appeared)}` : ""}`
    : `No visits yet${row.appeared ? ` · appeared ${dLong(row.appeared)}` : ""}`;
  const otherWhen = row.other_visits === 1
    ? `1 visit · ${dLong(row.other_last) ?? "—"}`
    : `${row.other_visits} visits · ${dLong(row.other_first) ?? "—"} → ${dLong(row.other_last) ?? "—"}`;
  return (
    <div className="mt-2.5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <Side tag="New" name={row.name} custno={row.custno} locno={row.locno} address={row.address} city={row.city} when={selfWhen} side="self" tone="new" />
        <ArrowLeftRight className="h-4 w-4 text-amber-600" aria-hidden="true" />
        <Side tag={row.other_is_company ? "Builder" : "On file"} name={row.other_name} custno={row.other_custno} locno={row.other_locno} address={row.other_address ?? null} city={row.other_city ?? row.city} when={otherWhen} side="other" tone="known" />
      </div>
      {streetsDiffer && (
        <div className="mt-1.5 text-[12px] font-semibold text-rose-700">Addresses differ — {differWords}. Same number on two streets is usually two different houses.</div>
      )}
    </div>
  );
}
